import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  requestLocationPermission,
  isGPSAvailable,
  getLocationPermissionState,
} from '../../services/location.service';
import LocationPermissionHelp from '../work-orders/gps/LocationPermissionHelp';
import {
  isFieldTeamRole,
  LOCATION_PROMPT_SESSION_KEY,
  getLocationPromptDismissedKey,
  getLocationPromptEnabledKey,
} from '../../constants/location-tracking-prompt';

const PROMPT_DELAY_MS = 1500;

/**
 * Prompts field team members to enable location after login.
 * Wording emphasizes limited use: only at job properties or offices.
 */
function LocationTrackingPrompt() {
  const { user, loading } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isEnabling, setIsEnabling] = useState(false);
  const [permissionState, setPermissionState] = useState('unknown');
  const [showPermissionHelp, setShowPermissionHelp] = useState(false);

  const shouldShowPrompt = useCallback(async () => {
    if (!user?.id || !isFieldTeamRole(user.role)) {
      return false;
    }
    if (!isGPSAvailable()) {
      return false;
    }
    const enabledKey = getLocationPromptEnabledKey(user.id);
    if (localStorage.getItem(enabledKey) === 'true') {
      return false;
    }
    const dismissedKey = getLocationPromptDismissedKey(user.id);
    if (localStorage.getItem(dismissedKey) === 'true') {
      return false;
    }
    if (sessionStorage.getItem(LOCATION_PROMPT_SESSION_KEY) === 'true') {
      return false;
    }
    const state = await getLocationPermissionState();
    setPermissionState(state);
    if (state === 'granted') {
      localStorage.setItem(enabledKey, 'true');
      sessionStorage.setItem(LOCATION_PROMPT_SESSION_KEY, 'true');
      return false;
    }
    return true;
  }, [user]);

  useEffect(() => {
    if (loading || !user) {
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      const show = await shouldShowPrompt();
      if (!cancelled && show) {
        setIsOpen(true);
      }
    }, PROMPT_DELAY_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [loading, user, shouldShowPrompt]);

  const handleDismissSession = () => {
    sessionStorage.setItem(LOCATION_PROMPT_SESSION_KEY, 'true');
    setIsOpen(false);
  };

  const handleDismissPermanent = () => {
    if (user?.id) {
      localStorage.setItem(getLocationPromptDismissedKey(user.id), 'true');
    }
    sessionStorage.setItem(LOCATION_PROMPT_SESSION_KEY, 'true');
    setIsOpen(false);
  };

  const handleEnableLocation = async () => {
    setIsEnabling(true);
    try {
      const granted = await requestLocationPermission();
      sessionStorage.setItem(LOCATION_PROMPT_SESSION_KEY, 'true');
      if (granted && user?.id) {
        localStorage.setItem(getLocationPromptEnabledKey(user.id), 'true');
        setIsOpen(false);
        return;
      }
      setPermissionState('denied');
      setShowPermissionHelp(true);
    } finally {
      setIsEnabling(false);
    }
  };

  if (!isOpen || !user) {
    return (
      <LocationPermissionHelp
        isOpen={showPermissionHelp}
        onClose={() => setShowPermissionHelp(false)}
      />
    );
  }

  const isDenied = permissionState === 'denied';

  return (
    <>
      <div
        className="fixed inset-0 z-[60] overflow-y-auto"
        role="dialog"
        aria-modal="true"
        aria-labelledby="location-tracking-prompt-title"
      >
        <div className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
          <div
            className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
            aria-hidden="true"
          />
          <span className="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true">
            &#8203;
          </span>
          <div className="inline-block w-full max-w-lg transform overflow-hidden rounded-lg bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:align-middle">
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-sky-100 sm:mx-0 sm:h-10 sm:w-10">
                  <svg
                    className="h-6 w-6 text-sky-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                </div>
                <div className="mt-3 flex-1 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <h3
                    id="location-tracking-prompt-title"
                    className="text-lg font-semibold leading-6 text-gray-900"
                  >
                    Enable location for job check-ins
                  </h3>
                  <div className="mt-3 space-y-3 text-sm text-gray-600">
                    <p>
                      Sweetgrass uses your phone&apos;s location <strong>only</strong> when you
                      update a job while you are at a rental property you are working on or at a
                      company office.
                    </p>
                    <p>
                      We <strong>do not</strong> track your location at home, while driving between
                      sites, or anywhere else.
                    </p>
                    <p>
                      This helps our team improve scheduling and reduce unnecessary drive time.
                    </p>
                    {isDenied && (
                      <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-800">
                        Location access is currently blocked. Tap &quot;How to enable&quot; below
                        to update your phone settings, then try again.
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowPermissionHelp(true)}
                    className="mt-3 text-sm font-medium text-sky-600 hover:text-sky-800 underline"
                  >
                    How to enable location on your phone
                  </button>
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-2 bg-gray-50 px-4 py-3 sm:flex-row sm:flex-row-reverse sm:px-6">
              <button
                type="button"
                onClick={handleEnableLocation}
                disabled={isEnabling}
                className="inline-flex w-full justify-center rounded-md border border-transparent bg-sky-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 disabled:opacity-50 sm:w-auto sm:text-sm"
              >
                {isEnabling ? 'Requesting…' : 'Enable location'}
              </button>
              <button
                type="button"
                onClick={handleDismissSession}
                disabled={isEnabling}
                className="inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 sm:w-auto sm:text-sm"
              >
                Not now
              </button>
            </div>
            <div className="border-t border-gray-100 px-4 pb-3 text-center sm:px-6">
              <button
                type="button"
                onClick={handleDismissPermanent}
                className="text-xs text-gray-500 hover:text-gray-700 underline"
              >
                Don&apos;t remind me again
              </button>
            </div>
          </div>
        </div>
      </div>
      <LocationPermissionHelp
        isOpen={showPermissionHelp}
        onClose={() => setShowPermissionHelp(false)}
      />
    </>
  );
}

export default LocationTrackingPrompt;
